import type {Middleware, Slice} from '@reduxjs/toolkit';
import {persistReducer} from 'redux-persist';

import localforage from 'localforage';

import {draftsReducerName, draftsReducer, type DraftsReducerState} from 'features/drafts/logic/slice';
import {draftsPersistMiddleware} from 'features/drafts/logic/drafts_persist_middleware';

import {sidebarForTeamLoadingSlice} from './sidebar';
import {channelPermissionsSlice, readOnlyChannelsSlice} from './channels';
import {deletePostsOnChannelSwitchMiddleware} from './posts/middlewares/delete_posts_on_channel_switch';
import {profilesSlice} from './profiles';
import {activitySlice} from './activity/slices/activity';
import {userThreadsSlice} from './threads/slices/user_threads';
import {userThreadsListenerMiddleware} from './threads/middlewares/user_threads_listener_middleware';
import {updatePresenceListenerMiddleware} from './websockets/middlewares/update_presence_middleware';
import type {OpenDesktopSettingsState} from './open_desktop';
import {openDesktopSettingsSlice} from './open_desktop';
import {type TeamSidebarState, teamSidebarSlice} from './team_sidebar';

import type {ProfilesState} from './profiles';
import type {ReadOnlyChannelsState} from './channels';
import {appVisibilitySlice} from './app_activity/slices/app_visibility_slice';
import {fatalErrorsReducerName, fatalErrorsReducer, type FatalErrorsState} from './fatalErrors';

import type {ActivityState} from './activity/slices/activity';
import {termsOfServiceSlice} from './terms_of_service/slices/terms_of_service';
import {adminUiSlice} from './admin';
import {editorSettingsSlice, type EditorSettingsState} from './editor/slices/editor_settings';
import {pushSubscriptionSlice, type PushSubscriptionState} from './push_notifications';

const persistReducerForSlice = <T>(slice: Slice<T>) =>
    persistReducer(
        {
            key: slice.name,
            storage: localforage,
        },
        slice.reducer,
    );
import {
    announcementBannerSlice,
    type AnnouncementBannerState,
} from './annoucement_banner/slices/announcement_banner_slice';

export const featuresReducers = {
    [adminUiSlice.name]: adminUiSlice.reducer,
    [fatalErrorsReducerName]: fatalErrorsReducer,
    [draftsReducerName]: draftsReducer,
    [sidebarForTeamLoadingSlice.name]: sidebarForTeamLoadingSlice.reducer,
    [channelPermissionsSlice.name]: channelPermissionsSlice.reducer,
    [readOnlyChannelsSlice.name]: readOnlyChannelsSlice.reducer,
    [profilesSlice.name]: profilesSlice.reducer,
    [appVisibilitySlice.name]: appVisibilitySlice.reducer,
    [activitySlice.name]: activitySlice.reducer,
    [userThreadsSlice.name]: userThreadsSlice.reducer,
    [termsOfServiceSlice.name]: termsOfServiceSlice.reducer,
    [pushSubscriptionSlice.name]: persistReducerForSlice(pushSubscriptionSlice),
    [openDesktopSettingsSlice.name]: persistReducerForSlice(openDesktopSettingsSlice),
    [teamSidebarSlice.name]: persistReducerForSlice(teamSidebarSlice),
    [editorSettingsSlice.name]: persistReducerForSlice(editorSettingsSlice),
    [announcementBannerSlice.name]: announcementBannerSlice.reducer,
};

type SidebarForTeamLoadingState = ReturnType<typeof sidebarForTeamLoadingSlice['getInitialState']>;
type ChannelPermissionsState = ReturnType<typeof channelPermissionsSlice['getInitialState']>;
type AppVisibilityState = ReturnType<typeof appVisibilitySlice['getInitialState']>;
type UserThreadsState = ReturnType<typeof userThreadsSlice['getInitialState']>;
type TermsOfServiceState = ReturnType<typeof termsOfServiceSlice['getInitialState']>

export type FeaturesReducersState = {
    [adminUiSlice.name]: ReturnType<typeof adminUiSlice['getInitialState']>;
    [fatalErrorsReducerName]: FatalErrorsState;
    [draftsReducerName]: DraftsReducerState;
    [sidebarForTeamLoadingSlice.name]: SidebarForTeamLoadingState;
    [channelPermissionsSlice.name]: ChannelPermissionsState;
    [readOnlyChannelsSlice.name]: ReadOnlyChannelsState;
    [profilesSlice.name]: ProfilesState;
    [appVisibilitySlice.name]: AppVisibilityState;
    [userThreadsSlice.name]: UserThreadsState;
    [activitySlice.name]: ActivityState;
    [announcementBannerSlice.name]: AnnouncementBannerState;
    [termsOfServiceSlice.name]: TermsOfServiceState;
    [openDesktopSettingsSlice.name]: OpenDesktopSettingsState;
    [teamSidebarSlice.name]: TeamSidebarState;
    [editorSettingsSlice.name]: EditorSettingsState;
    [pushSubscriptionSlice.name]: PushSubscriptionState;
};

export const featuresMiddlewares: Middleware[] = [
    draftsPersistMiddleware,
    deletePostsOnChannelSwitchMiddleware,
    userThreadsListenerMiddleware,
    updatePresenceListenerMiddleware,
];
